import React, { useCallback, useEffect, useState } from 'react';
import html2canvas from 'html2canvas';
import { setCapturaPendiente } from './utils/capturaPendiente';
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronsDownUp,
  Settings,
  CalendarClock,
  MapPin,
  ScanLine,
  LayoutGrid,
  Sprout,
  ClipboardList,
  ClipboardCheck,
  BarChart2,
  Boxes,
  CalendarCheck,
  FileText,
  Layers,
  Package,
  Tractor,
  BookOpenCheck,
  Archive,
  Clock,
  Database,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  Leaf,
  MessageCircle,
  Coins,
  Sprout as BiologicalAssetIcon,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { supabase } from './supabase';
import { useAppPermisos, type AppPermisos } from './hooks/useAppPermisos';
import { useEsSuperUsuario } from './hooks/useEsSuperUsuario';
import { EmpresaProvider } from './context/EmpresaContext';
import { NavbarSubtituloProvider } from './context/NavbarSubtituloContext';
import { Login } from './components/Login';
import { RecoveryPassword } from './components/RecoveryPassword';
import { buildApiCandidates } from './utils/apiUrl';
import type { LoginResult } from './components/Login';

import { ApplicacionesDashboard } from './pages/aplicaciones/ApplicacionesDashboard';
import { DetalleBoleta } from './pages/aplicaciones/DetalleBoleta';
import { ResumenSemanal } from './pages/aplicaciones/ResumenSemanal';
import { ParcelasFinca } from './pages/aplicaciones/ParcelasFinca';
import { DronImporter } from './pages/aplicaciones/DronImporter';
import { DronMosaico } from './pages/aplicaciones/DronMosaico';
import { ConteoPlantasV2Page } from './pages/aplicaciones/ConteoPlantasV2Page';
import { ConteoPlantasV3TestPage } from './pages/aplicaciones/ConteoPlantasV3TestPage';
import { ConteoPlantasV4SimplePage } from './pages/aplicaciones/ConteoPlantasV4SimplePage';
import { ConteoPlantasV5SimplePage } from './pages/aplicaciones/ConteoPlantasV5SimplePage';
import { ConfiguracionGeneral } from './pages/aplicaciones/ConfiguracionGeneral';
import { CronologiaParametrosPage } from './pages/aplicaciones/CronologiaParametrosPage';
import { BoletasAplicacionPage } from './pages/aplicaciones/BoletasAplicacionPage';
import { BoletaManualPage } from './pages/aplicaciones/BoletaManualPage';
import { CedulasAplicacionPage } from './pages/aplicaciones/CedulasAplicacionPage';
import { CedulasAplicarDirectoPage } from './pages/aplicaciones/CedulasAplicarDirectoPage';
import { BodegaPage } from './pages/aplicaciones/BodegaPage';
import { TomaInventarioPage } from './pages/aplicaciones/TomaInventarioPage';
import { BoomeroPage } from './pages/aplicaciones/BoomeroPage';
import { EstadiosPage } from './pages/aplicaciones/EstadiosPage';
import { AplicacionesManualesPage } from './pages/aplicaciones/AplicacionesManualesPage';
import { PlanManejoPage } from './pages/aplicaciones/PlanManejoPage';
import { PlanCostosPage } from './pages/aplicaciones/PlanCostosPage';
import { CatalogoProductosPage } from './pages/aplicaciones/CatalogoProductosPage';
import { DepuracionInventarioPage } from './pages/aplicaciones/DepuracionInventarioPage';
import { AuditoriaSaldosPage } from './pages/aplicaciones/AuditoriaSaldosPage';
import { FincaLoteBloquePage } from './pages/aplicaciones/FincaLoteBloquePage';
import { FincasPage } from './pages/aplicaciones/FincasPage';
import { EstadiosVfpPage } from './pages/aplicaciones/EstadiosVfpPage';
import { CiclosCatalogoPage } from './pages/aplicaciones/CiclosCatalogoPage';
import { ControlPinaRolesPage } from './pages/aplicaciones/ControlPinaRolesPage';
import { SyncVfpPage } from './pages/aplicaciones/SyncVfpPage';
import { LaboresCampoPage } from './pages/aplicaciones/LaboresCampoPage';
import { BoletasLaboresPage } from './pages/aplicaciones/BoletasLaboresPage';
import { BoletasMaquinariaPage } from './pages/aplicaciones/BoletasMaquinariaPage';
import { JornadasLaboresPage } from './pages/aplicaciones/JornadasLaboresPage';
import { ReporteJornadasAprobadasPage } from './pages/aplicaciones/ReporteJornadasAprobadasPage';
import { CierreSemanalFincaPage } from './pages/aplicaciones/CierreSemanalFincaPage';
import { CosteoContableLaboresPage } from './pages/aplicaciones/CosteoContableLaboresPage';
import { LotesCosteoLaboresPage } from './pages/aplicaciones/LotesCosteoLaboresPage';
import { ProgramaMuestreosPage } from './pages/aplicaciones/ProgramaMuestreosPage';
import { MisMuestreosPage } from './pages/aplicaciones/MisMuestreosPage';
import { ReporteMuestreosPage } from './pages/aplicaciones/ReporteMuestreosPage';
import { CatalogoLaboresMuestreosPage } from './pages/aplicaciones/CatalogoLaboresMuestreosPage';
import { ReporteCostoLotePage } from './pages/aplicaciones/ReporteCostoLotePage';
import { WhatsAppSenderPage } from './pages/aplicaciones/WhatsAppSenderPage';
import { ConteoCalibradoPage } from './pages/aplicaciones/ConteoCalibradoPage';
import { ConteoPlantasV8Page } from './pages/aplicaciones/ConteoPlantasV8Page';
import { AjusteSolicitudPage } from './pages/aplicaciones/AjusteSolicitudPage';
import { AnclaSolicitudPage } from './pages/aplicaciones/AnclaSolicitudPage';
import { ActivoBiologicoInsumosPage } from './pages/aplicaciones/ActivoBiologicoInsumosPage';
import { CosteoProduccionABPage } from './pages/aplicaciones/CosteoProduccionABPage';
import { LineaTiempoLotePage } from './pages/aplicaciones/LineaTiempoLotePage';


type AppRoute =
  | 'dashboard'
  | 'aplicaciones.estadios'
  | 'aplicaciones.linea-tiempo'
  | 'aplicaciones.boletas'
  | 'aplicaciones.manual'
  | 'aplicaciones.cedulas'
  | 'aplicaciones.aplicar-directo'
  | 'aplicaciones.bodega'
  | 'aplicaciones.boomero'
  | 'aplicaciones.toma-inventario'
  | 'aplicaciones.whatsapp'
  | 'aplicaciones.resumen'
  | 'aplicaciones.boleta'
  | 'aplicaciones.plan-manejo'
  | 'aplicaciones.plan-costos'
  | 'aplicaciones.productos'
  | 'aplicaciones.depuracion-inventario'
  | 'aplicaciones.auditoria-saldos'
  | 'aplicaciones.lote-bloque'
  | 'aplicaciones.fincas'
  | 'aplicaciones.estadios-vfp'
  | 'aplicaciones.aplicaciones-manuales'
  | 'aplicaciones.ciclos'
  | 'labores.catalogo'
  | 'labores.boletas'
  | 'labores.jornadas'
  | 'labores.maquinaria'
  | 'labores.costeo-contable'
  | 'labores.lotes-costeo'
  | 'labores.reporte-jornadas'
  | 'labores.cierre-semanal'
  | 'labores.muestreos'
  | 'labores.mis-muestreos'
  | 'labores.reporte-muestreos'
  | 'labores.catalogo-muestreos'
  | 'reportes.costo-lote'
  | 'reportes.activo-biologico'
  | 'reportes.costeo-produccion-ab'
  | 'aplicaciones.parcelas'
  | 'aplicaciones.dron-importer'
  | 'aplicaciones.dron-mosaico'
  | 'aplicaciones.conteo'
  | 'aplicaciones.conteo-v3-test'
  | 'aplicaciones.conteo-v4-simple'
  | 'aplicaciones.conteo-v5-simple'
  | 'aplicaciones.conteo-calibrado'
  | 'aplicaciones.conteo-v8'
  | 'aplicaciones.roles'
  | 'aplicaciones.sync-vfp'
  | 'aplicaciones.config-general'
  | 'aplicaciones.cronologia-parametros';

interface Usuario {
  id: string;
  usuario_id?: string;
  username: string;
  email: string;
  nombre: string;
  labor: string;
  empresa_id: string;
  es_superusuario?: boolean;
}

interface Palette {
  id: string;
  nombre: string;
  colorScheme?: 'dark' | 'light';
  bgDark: string;
  bgDark2: string;
  accentMain: string;
  accentDim: string;
  accentSoft: string;
  accentMuted: string;
  gray100: string;
  gray200: string;
  gray400: string;
  gray600: string;
  gray800: string;
}

const PALETTES: Palette[] = [
  {
    id: 'selva',
    nombre: 'Selva',
    bgDark: '#0b1120',
    bgDark2: '#070d18',
    accentMain: '#16a34a',
    accentDim: '#4ade80',
    accentSoft: '#0f2a1a',
    accentMuted: '#1f4a2c',
    gray100: '#d8e3ef',
    gray200: '#7c93ae',
    gray400: '#3a526a',
    gray600: '#1c2e46',
    gray800: '#131f33',
  },
  {
    id: 'sky',
    nombre: 'Cielo',
    bgDark: '#0f172a',
    bgDark2: '#17223a',
    accentMain: '#38bdf8',
    accentDim: '#0284c7',
    accentSoft: '#0b2f45',
    accentMuted: '#154c6a',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#94a3b8',
    gray600: '#475569',
    gray800: '#1e293b',
  },
  {
    id: 'dark',
    nombre: 'Dark',
    bgDark: '#05070c',
    bgDark2: '#0a0f1a',
    accentMain: '#60a5fa',
    accentDim: '#3b82f6',
    accentSoft: '#13243d',
    accentMuted: '#1b3353',
    gray100: '#e5e7eb',
    gray200: '#c7ced8',
    gray400: '#8b95a7',
    gray600: '#5f6b80',
    gray800: '#2d3748',
  },
  {
    id: 'claro',
    nombre: 'Claro',
    colorScheme: 'light',
    bgDark: '#ffffff',
    bgDark2: '#f8fafc',
    accentMain: '#2563eb',
    accentDim: '#1d4ed8',
    accentSoft: '#eff6ff',
    accentMuted: '#bfdbfe',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray400: '#64748b',
    gray600: '#334155',
    gray800: '#0f172a',
  },
];

const THEME_KEY = 'mya-color-theme';
// Ícono de piña (emoji en SVG) — usado como fallback de logo y favicon.
const APP_FAVICON_DATA_URI =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ctext y='78' font-size='80'%3E%F0%9F%8D%8D%3C/text%3E%3C/svg%3E";
const FALLBACK_LOGO = APP_FAVICON_DATA_URI;
const INTRO_GIF = `${process.env.PUBLIC_URL}/branding/fondo.gif`;
const INTRO_DURATION_MS = 7200;
const INTRO_EXIT_MS = 420;
const LAST_EMPRESA_KEY = 'mya-aplicaciones-last-empresa-id';
const DEFAULT_EMPRESA_ID = Number(process.env.REACT_APP_EMPRESA_ID || 3);

function getPalette(id: string): Palette {
  return PALETTES.find((p) => p.id === id) || PALETTES[0];
}

function getSemanaActualLabel(fecha = new Date()): string {
  const d = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${String(week).padStart(2, '0')}-${String(d.getUTCFullYear()).slice(-2)}`;
}

function applyPalette(p: Palette) {
  const r = document.documentElement.style;
  const isLight = p.id === 'claro';
  const darkSurfaces: Record<string, { overlay: string; raised: string; deep: string }> = {
    selva: { overlay: '#0d1626', raised: '#101b2e', deep: '#0a121f' },
    sky: { overlay: '#13233b', raised: '#1a2d49', deep: '#101c30' },
    dark: { overlay: '#0f1726', raised: '#141d2f', deep: '#0a111d' },
  };
  const dark = darkSurfaces[p.id] || { overlay: p.bgDark2, raised: p.bgDark2, deep: p.bgDark };
  const surfaceBase = p.bgDark;
  const surfacePanel = p.bgDark2;
  const surfaceOverlay = isLight ? '#ffffff' : dark.overlay;
  const surfaceRaised = isLight ? '#f8fafc' : dark.raised;
  const surfaceDeep = isLight ? '#eef2f7' : dark.deep;
  const line = isLight ? '#d7e0ea' : 'rgba(255,255,255,0.14)';
  const lineDim = isLight ? '#e5ebf2' : 'rgba(255,255,255,0.08)';
  const ink = isLight ? '#0f172a' : p.gray100;
  const inkMuted = isLight ? '#334155' : p.gray200;
  const inkFaint = isLight ? '#64748b' : p.gray400;
  const accentBg = isLight ? p.accentSoft : `${p.accentMain}22`;
  const accentTxt = isLight ? p.accentDim : p.accentMain;

  r.setProperty('--bg-dark', p.bgDark);
  r.setProperty('--bg-dark2', p.bgDark2);
  r.setProperty('--green-main', p.accentMain);
  r.setProperty('--green-dim', p.accentDim);
  r.setProperty('--green-soft', p.accentSoft);
  r.setProperty('--green-muted', p.accentMuted);
  r.setProperty('--gray-100', p.gray100);
  r.setProperty('--gray-200', p.gray200);
  r.setProperty('--gray-400', p.gray400);
  r.setProperty('--gray-600', p.gray600);
  r.setProperty('--gray-800', p.gray800);

  r.setProperty('--surface-base', surfaceBase);
  r.setProperty('--surface-raised', surfaceRaised);
  r.setProperty('--surface-overlay', surfaceOverlay);
  r.setProperty('--surface-deep', surfaceDeep);
  r.setProperty('--ink', ink);
  r.setProperty('--ink-muted', inkMuted);
  r.setProperty('--ink-faint', inkFaint);
  r.setProperty('--line', line);
  r.setProperty('--line-dim', lineDim);

  r.setProperty('--emp-bg-main', surfaceBase);
  r.setProperty('--emp-bg-panel', surfacePanel);
  r.setProperty('--emp-border', line);
  r.setProperty('--emp-accent-bg', accentBg);
  r.setProperty('--emp-accent', p.accentMain);
  r.setProperty('--emp-accent-txt', accentTxt);
  // Texto que va ENCIMA del color de acento sólido (botones primarios). El acento
  // es un color saturado en cualquier tema → texto blanco. Faltaba definirla y
  // varios botones quedaban con texto oscuro sobre el acento (notorio en tema claro).
  r.setProperty('--emp-accent-on', '#ffffff');

  // Semantic dashboard tones per theme
  r.setProperty('--tone-success-bg', isLight ? '#ecfdf3' : '#0e2b1f');
  r.setProperty('--tone-success-border', isLight ? '#86efac' : '#1f6d4b');
  r.setProperty('--tone-success-text', isLight ? '#15803d' : '#4ade80');
  r.setProperty('--tone-success-ink', isLight ? '#14532d' : '#d8e3ef');

  r.setProperty('--tone-warn-bg', isLight ? '#fffbeb' : '#1f160a');
  r.setProperty('--tone-warn-border', isLight ? '#fcd34d' : '#7a4a12');
  r.setProperty('--tone-warn-text', isLight ? '#b45309' : '#f59e0b');
  r.setProperty('--tone-warn-ink', isLight ? '#78350f' : '#d8e3ef');

  r.setProperty('--tone-info-bg', isLight ? '#eff6ff' : '#0d1d35');
  r.setProperty('--tone-info-border', isLight ? '#93c5fd' : '#1f4f96');
  r.setProperty('--tone-info-text', isLight ? '#2563eb' : '#60a5fa');
  r.setProperty('--tone-info-ink', isLight ? '#1e3a8a' : '#d8e3ef');

  r.setProperty('--tone-purple-bg', isLight ? '#f5f3ff' : '#14173a');
  r.setProperty('--tone-purple-border', isLight ? '#c4b5fd' : '#4b3ca8');
  r.setProperty('--tone-purple-text', isLight ? '#7c3aed' : '#a78bfa');
  r.setProperty('--tone-purple-ink', isLight ? '#4c1d95' : '#d8e3ef');
  document.documentElement.style.colorScheme = p.colorScheme || 'dark';
}

function ThemeSwitcher({
  paletteId,
  onChange,
  compact = false,
}: {
  paletteId: string;
  onChange: (id: string) => void;
  compact?: boolean;
}) {
  const p = getPalette(paletteId);
  const cycle = () => {
    const idx = PALETTES.findIndex((x) => x.id === paletteId);
    onChange(PALETTES[(idx + 1) % PALETTES.length].id);
  };

  return (
    <button
      onClick={cycle}
      title={`Tema: ${p.nombre} - click para cambiar`}
      className={`flex items-center rounded-full text-xs font-medium transition-colors ${
        compact ? 'gap-1 px-2 py-1' : 'gap-1.5 px-2.5 py-1'
      }`}
      style={{ background: p.accentSoft, border: `1px solid ${p.accentMain}44`, color: p.accentDim }}
    >
      <span
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${p.accentMain}, ${p.accentDim})` }}
      />
      <span className={compact ? 'hidden sm:inline' : ''}>{p.nombre}</span>
    </button>
  );
}

interface MenuItem {
  id: string;
  label: string;
  route?: AppRoute;
  icon: React.ReactNode;
  children?: { id: string; label: string; route: AppRoute; icon: React.ReactNode }[];
}

const MENU: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    route: 'dashboard',
    icon: <ClipboardList size={18} />,
  },
  {
    id: 'operaciones',
    label: 'Operaciones',
    icon: <FileText size={18} />,
    children: [
      { id: 'aplicaciones.estadios', label: 'Estadios', route: 'aplicaciones.estadios', icon: <Sprout size={16} /> },
      { id: 'aplicaciones.linea-tiempo', label: 'Línea de tiempo por lote', route: 'aplicaciones.linea-tiempo', icon: <CalendarClock size={16} /> },
      { id: 'aplicaciones.boletas', label: 'Boletas de Aplicacion', route: 'aplicaciones.boletas', icon: <FileText size={16} /> },
      { id: 'aplicaciones.manual', label: 'Boleta Manual', route: 'aplicaciones.manual', icon: <FileText size={16} /> },
      { id: 'aplicaciones.cedulas', label: 'Cedulas (Historial)', route: 'aplicaciones.cedulas', icon: <ClipboardList size={16} /> },
      { id: 'aplicaciones.aplicar-directo', label: 'Aplicar Directo', route: 'aplicaciones.aplicar-directo', icon: <ClipboardCheck size={16} /> },
      { id: 'aplicaciones.bodega', label: 'Terminal Bodega', route: 'aplicaciones.bodega', icon: <Boxes size={16} /> },
      { id: 'aplicaciones.boomero', label: 'Terminal Boomero', route: 'aplicaciones.boomero', icon: <Leaf size={16} /> },
      { id: 'aplicaciones.whatsapp', label: 'WhatsApp', route: 'aplicaciones.whatsapp', icon: <MessageCircle size={16} /> },
    ],
  },
  {
    id: 'muestreos',
    label: 'Muestreos',
    icon: <CalendarCheck size={18} />,
    children: [
      { id: 'labores.muestreos', label: 'Programa de Muestreos', route: 'labores.muestreos', icon: <CalendarCheck size={16} /> },
      { id: 'labores.mis-muestreos', label: 'Mis Muestreos', route: 'labores.mis-muestreos', icon: <ClipboardCheck size={16} /> },
      { id: 'labores.reporte-muestreos', label: 'Consolidado de Muestreos', route: 'labores.reporte-muestreos', icon: <BarChart2 size={16} /> },
      { id: 'labores.catalogo-muestreos', label: 'Catálogo de Muestreos', route: 'labores.catalogo-muestreos', icon: <ClipboardList size={16} /> },
    ],
  },
  {
    id: 'labores-jornadas',
    label: 'Labores y Jornadas',
    icon: <Tractor size={18} />,
    children: [
      { id: 'labores.catalogo', label: 'Catalogo de Labores', route: 'labores.catalogo', icon: <ClipboardList size={16} /> },
      { id: 'labores.boletas', label: 'Boletas de Labores', route: 'labores.boletas', icon: <FileText size={16} /> },
      { id: 'labores.maquinaria', label: 'Maquinaria', route: 'labores.maquinaria', icon: <Tractor size={16} /> },
      { id: 'labores.jornadas', label: 'Jornadas', route: 'labores.jornadas', icon: <CalendarCheck size={16} /> },
      { id: 'labores.reporte-jornadas', label: 'Reporte Jornadas', route: 'labores.reporte-jornadas', icon: <Clock size={16} /> },
      { id: 'labores.cierre-semanal', label: 'Cierre Semanal (Planilla)', route: 'labores.cierre-semanal', icon: <CalendarCheck size={16} /> },
      { id: 'labores.costeo-contable', label: 'Costeo Contable', route: 'labores.costeo-contable', icon: <BookOpenCheck size={16} /> },
      { id: 'labores.lotes-costeo', label: 'Lotes de Costeo', route: 'labores.lotes-costeo', icon: <Archive size={16} /> },
    ],
  },
  {
    id: 'inventario',
    label: 'Inventario',
    icon: <Boxes size={18} />,
    children: [
      { id: 'aplicaciones.toma-inventario', label: 'Toma de Inventario', route: 'aplicaciones.toma-inventario', icon: <Boxes size={16} /> },
      { id: 'aplicaciones.productos', label: 'Productos', route: 'aplicaciones.productos', icon: <Package size={16} /> },
      { id: 'aplicaciones.depuracion-inventario', label: 'Depuración de Inventario', route: 'aplicaciones.depuracion-inventario', icon: <Sparkles size={16} /> },
      { id: 'aplicaciones.auditoria-saldos', label: 'Auditoría de Saldos', route: 'aplicaciones.auditoria-saldos', icon: <ClipboardCheck size={16} /> },
    ],
  },
  {
    id: 'campo',
    label: 'Campo',
    icon: <MapPin size={18} />,
    children: [
      { id: 'aplicaciones.fincas', label: 'Fincas', route: 'aplicaciones.fincas', icon: <MapPin size={16} /> },
      { id: 'aplicaciones.lote-bloque', label: 'Lote / Bloque', route: 'aplicaciones.lote-bloque', icon: <MapPin size={16} /> },
      { id: 'aplicaciones.plan-manejo', label: 'Plan de Manejo', route: 'aplicaciones.plan-manejo', icon: <Layers size={16} /> },
      { id: 'aplicaciones.ciclos', label: 'Ciclos', route: 'aplicaciones.ciclos', icon: <Layers size={16} /> },
      { id: 'aplicaciones.estadios-vfp', label: 'Estadios VFP', route: 'aplicaciones.estadios-vfp', icon: <Sprout size={16} /> },
      { id: 'aplicaciones.aplicaciones-manuales', label: 'Aplicaciones Manuales', route: 'aplicaciones.aplicaciones-manuales', icon: <ClipboardList size={16} /> },
      { id: 'aplicaciones.parcelas', label: 'Parcelas', route: 'aplicaciones.parcelas', icon: <MapPin size={16} /> },
    ],
  },
  {
    id: 'reportes',
    label: 'Reportes',
    icon: <BarChart2 size={18} />,
    children: [
      { id: 'aplicaciones.resumen', label: 'Resumen Semanal', route: 'aplicaciones.resumen', icon: <BarChart2 size={16} /> },
      { id: 'reportes.costo-lote', label: 'Costo por Lote', route: 'reportes.costo-lote', icon: <BarChart2 size={16} /> },
      { id: 'reportes.activo-biologico', label: 'Activo Biológico', route: 'reportes.activo-biologico', icon: <BiologicalAssetIcon size={16} /> },
      { id: 'reportes.costeo-produccion-ab', label: 'Costeo Producción AB', route: 'reportes.costeo-produccion-ab', icon: <BiologicalAssetIcon size={16} /> },
      { id: 'aplicaciones.plan-costos', label: 'Costos del Plan', route: 'aplicaciones.plan-costos', icon: <Coins size={16} /> },
    ],
  },
  {
    id: 'conteo',
    label: 'Conteo (Dron/IA)',
    icon: <ScanLine size={18} />,
    children: [
      { id: 'aplicaciones.dron-importer', label: 'Dron -> Poligono', route: 'aplicaciones.dron-importer', icon: <ScanLine size={16} /> },
      { id: 'aplicaciones.dron-mosaico', label: 'Dron -> Mosaico', route: 'aplicaciones.dron-mosaico', icon: <LayoutGrid size={16} /> },
      // ⚠️ TEMPORAL: varias versiones de conteo visibles para identificar la BUENA.
      // Probar en campo cuál da el conteo correcto y luego dejar SOLO esa. Ver memoria.
      { id: 'aplicaciones.conteo-calibrado', label: '⭐ Conteo Calibrado (Agripix)', route: 'aplicaciones.conteo-calibrado', icon: <Sprout size={16} /> },
      { id: 'aplicaciones.conteo-v8', label: '⭐ Conteo V8 (más nueva)', route: 'aplicaciones.conteo-v8', icon: <Sprout size={16} /> },
      { id: 'aplicaciones.conteo', label: 'Conteo V2 (vieja)', route: 'aplicaciones.conteo', icon: <Sprout size={16} /> },
      { id: 'aplicaciones.conteo-v3-test', label: 'Conteo V3 (Prueba)', route: 'aplicaciones.conteo-v3-test', icon: <Sprout size={16} /> },
      { id: 'aplicaciones.conteo-v4-simple', label: 'Conteo V4 (Prueba)', route: 'aplicaciones.conteo-v4-simple', icon: <Sprout size={16} /> },
      { id: 'aplicaciones.conteo-v5-simple', label: 'Conteo V5 (Prueba)', route: 'aplicaciones.conteo-v5-simple', icon: <Sprout size={16} /> },
    ],
  },
  {
    id: 'configuracion',
    label: 'Configuracion',
    icon: <Settings size={18} />,
    children: [
      { id: 'aplicaciones.roles', label: 'Roles', route: 'aplicaciones.roles', icon: <ShieldCheck size={16} /> },
      { id: 'aplicaciones.sync-vfp', label: 'Sync VFP', route: 'aplicaciones.sync-vfp', icon: <Database size={16} /> },
      { id: 'aplicaciones.config-general', label: 'Configuracion General', route: 'aplicaciones.config-general', icon: <Settings size={16} /> },
      { id: 'aplicaciones.cronologia-parametros', label: 'Cronología del Cultivo', route: 'aplicaciones.cronologia-parametros', icon: <CalendarClock size={16} /> },
    ],
  },
];

const ROUTE_PARENT: Partial<Record<AppRoute, AppRoute>> = {
  'aplicaciones.estadios': 'dashboard',
  'aplicaciones.linea-tiempo': 'dashboard',
  'aplicaciones.boletas': 'dashboard',
  'aplicaciones.manual': 'dashboard',
  'aplicaciones.cedulas': 'dashboard',
  'aplicaciones.aplicar-directo': 'dashboard',
  'aplicaciones.bodega': 'dashboard',
  'aplicaciones.boomero': 'dashboard',
  'aplicaciones.toma-inventario': 'dashboard',
  'aplicaciones.whatsapp': 'dashboard',
  'aplicaciones.resumen': 'dashboard',
  'aplicaciones.plan-manejo': 'dashboard',
  'aplicaciones.plan-costos': 'dashboard',
  'aplicaciones.productos': 'dashboard',
  'aplicaciones.depuracion-inventario': 'dashboard',
  'aplicaciones.auditoria-saldos': 'dashboard',
  'aplicaciones.lote-bloque': 'dashboard',
  'aplicaciones.fincas': 'dashboard',
  'aplicaciones.estadios-vfp': 'dashboard',
  'aplicaciones.aplicaciones-manuales': 'dashboard',
  'aplicaciones.ciclos': 'dashboard',
  'labores.catalogo': 'dashboard',
  'labores.boletas': 'dashboard',
  'labores.jornadas': 'dashboard',
  'labores.maquinaria': 'dashboard',
  'labores.costeo-contable': 'dashboard',
  'labores.lotes-costeo': 'dashboard',
  'labores.reporte-jornadas': 'dashboard',
  'labores.cierre-semanal': 'dashboard',
  'labores.muestreos': 'dashboard',
  'labores.mis-muestreos': 'dashboard',
  'labores.reporte-muestreos': 'dashboard',
  'labores.catalogo-muestreos': 'dashboard',
  'reportes.activo-biologico': 'dashboard',
  'reportes.costeo-produccion-ab': 'dashboard',
  'aplicaciones.parcelas': 'dashboard',
  'aplicaciones.config-general': 'dashboard',
  'aplicaciones.cronologia-parametros': 'dashboard',
  'aplicaciones.dron-importer': 'dashboard',
  'aplicaciones.dron-mosaico': 'dashboard',
  'aplicaciones.conteo': 'dashboard',
  'aplicaciones.conteo-v3-test': 'dashboard',
  'aplicaciones.conteo-v4-simple': 'dashboard',
  'aplicaciones.conteo-v5-simple': 'dashboard',
  'aplicaciones.conteo-calibrado': 'dashboard',
  'aplicaciones.conteo-v8': 'dashboard',
  'aplicaciones.roles': 'dashboard',
  'aplicaciones.sync-vfp': 'dashboard',
};

// Permiso mínimo para ENTRAR a cada ruta (no solo para ver botones dentro
// de la página). Antes casi ninguna pantalla se chequeaba a este nivel —
// cualquier usuario logueado, sin importar su rol, podía abrir cualquier
// pantalla del menú con solo escribir la ruta. Un valor con varios permisos
// es "basta con tener UNO de estos" (OR) — se usa en los grupos operativos
// donde no hay un único flag dedicado, para no bloquear a un rol real que
// sí necesita entrar solo porque le falta uno específico. Sync VFP se
// maneja aparte (exige Super Usuario, no un permiso de rol) y ya se
// autoprotege dentro de la página.
const ROUTE_PERMISSION: Partial<Record<AppRoute, keyof AppPermisos | (keyof AppPermisos)[]>> = {
  // Operaciones (boletas de aplicación, bodega, boomero) — cualquier rol
  // operativo real tiene al menos uno de estos.
  'aplicaciones.estadios': ['perm_generar_boletas', 'perm_vob', 'perm_despachar', 'perm_confirmar_aplicacion'],
  'aplicaciones.linea-tiempo': ['perm_generar_boletas', 'perm_vob', 'perm_ver_reportes', 'perm_despachar', 'perm_confirmar_aplicacion'],
  'aplicaciones.boletas': ['perm_generar_boletas', 'perm_vob', 'perm_despachar', 'perm_confirmar_aplicacion'],
  'aplicaciones.manual': ['perm_generar_boletas', 'perm_vob', 'perm_despachar', 'perm_confirmar_aplicacion'],
  'aplicaciones.cedulas': ['perm_generar_boletas', 'perm_vob', 'perm_ver_reportes', 'perm_despachar', 'perm_confirmar_aplicacion'],
  'aplicaciones.aplicar-directo': ['perm_generar_boletas', 'perm_vob', 'perm_confirmar_aplicacion', 'perm_despachar'],
  'aplicaciones.bodega': ['perm_despachar', 'perm_vob', 'perm_generar_boletas', 'perm_confirmar_aplicacion'],
  'aplicaciones.boomero': ['perm_confirmar_aplicacion', 'perm_reporte_campo', 'perm_vob', 'perm_generar_boletas'],
  // Muestreos
  'labores.muestreos': ['perm_muestrear', 'perm_vob'],
  'labores.mis-muestreos': ['perm_muestrear', 'perm_vob'],
  'labores.reporte-muestreos': ['perm_muestrear', 'perm_ver_reportes', 'perm_vob'],
  'labores.catalogo-muestreos': ['perm_muestrear', 'perm_vob'],
  // Labores y Jornadas — no hay flag dedicado a "labores"; se usa el
  // conjunto operativo/administrativo más cercano. perm_ver_catalogo entra
  // acá también porque el rol Inventarios (costos/XML/labores) lo usa a diario.
  'labores.catalogo': ['perm_vob', 'perm_administrar', 'perm_ver_catalogo'],
  'labores.boletas': ['perm_generar_boletas', 'perm_boletas_labores', 'perm_reporte_campo', 'perm_vob', 'perm_ver_catalogo'],
  'labores.maquinaria': ['perm_generar_boletas', 'perm_reporte_campo', 'perm_vob', 'perm_ver_catalogo'],
  'labores.jornadas': ['perm_generar_boletas', 'perm_reporte_campo', 'perm_vob', 'perm_ver_catalogo'],
  'labores.reporte-jornadas': ['perm_ver_reportes', 'perm_vob', 'perm_ver_catalogo'],
  'labores.cierre-semanal': ['perm_cierre_planilla', 'perm_vob', 'perm_boletas_labores'],
  'labores.costeo-contable': ['perm_ver_reportes', 'perm_vob', 'perm_administrar', 'perm_ver_catalogo'],
  'labores.lotes-costeo': ['perm_ver_reportes', 'perm_vob', 'perm_administrar', 'perm_ver_catalogo'],
  // Conteo (dron/IA) — herramienta técnica, requiere al menos catálogo o V°B°.
  'aplicaciones.dron-importer': ['perm_ver_catalogo', 'perm_vob'],
  'aplicaciones.dron-mosaico': ['perm_ver_catalogo', 'perm_vob'],
  'aplicaciones.conteo-calibrado': ['perm_ver_catalogo', 'perm_vob'],
  'aplicaciones.conteo-v8': ['perm_ver_catalogo', 'perm_vob'],
  'aplicaciones.conteo': ['perm_ver_catalogo', 'perm_vob'],
  'aplicaciones.conteo-v3-test': ['perm_ver_catalogo', 'perm_vob'],
  'aplicaciones.conteo-v4-simple': ['perm_ver_catalogo', 'perm_vob'],
  'aplicaciones.conteo-v5-simple': ['perm_ver_catalogo', 'perm_vob'],
  // Inventario
  'aplicaciones.toma-inventario': 'perm_ver_catalogo',
  'aplicaciones.productos': 'perm_ver_catalogo',
  'aplicaciones.depuracion-inventario': 'perm_ver_catalogo',
  'aplicaciones.auditoria-saldos': 'perm_ver_catalogo',
  // Campo (catálogo/estructura — finca, lote, bloque, plan, ciclos)
  'aplicaciones.fincas': 'perm_ver_catalogo',
  'aplicaciones.lote-bloque': 'perm_ver_catalogo',
  'aplicaciones.plan-manejo': 'perm_ver_catalogo',
  'aplicaciones.ciclos': 'perm_ver_catalogo',
  'aplicaciones.estadios-vfp': 'perm_ver_catalogo',
  'aplicaciones.aplicaciones-manuales': 'perm_ver_catalogo',
  'aplicaciones.parcelas': 'perm_ver_catalogo',
  // Reportes
  'aplicaciones.resumen': 'perm_ver_reportes',
  'reportes.costo-lote': 'perm_ver_reportes',
  'reportes.activo-biologico': 'perm_ver_reportes',
  'reportes.costeo-produccion-ab': 'perm_ver_reportes',
  'aplicaciones.plan-costos': 'perm_ver_reportes',
  // Configuración
  'aplicaciones.roles': 'perm_administrar',
  'aplicaciones.config-general': 'perm_administrar',
  'aplicaciones.cronologia-parametros': 'perm_vob',
  'aplicaciones.sync-vfp': 'perm_sync_vfp',
};

// Portero de ruta: componente real (no una función suelta) para que
// useAppPermisos (que depende de EmpresaContext) resuelva bien — se coloca
// en el JSX ya dentro de <EmpresaProvider>, a diferencia de renderPage()
// que es solo una función auxiliar del componente App.
// Rutas alcanzables sin ningún rol de Aplicaciones asignado — el dashboard
// (para no dejar una pantalla en blanco al entrar) y WhatsApp (mensajería
// básica, no expone datos operativos).
const RUTAS_SIN_ROL: AppRoute[] = ['dashboard', 'aplicaciones.whatsapp'];

function RouteGate({ route, children }: { route: AppRoute; children: React.ReactNode }) {
  const permisos = useAppPermisos();
  const { esSuperUsuario, loading: cargandoSU } = useEsSuperUsuario();
  if (permisos.loading || cargandoSU) return null;

  // El Super Usuario (tabla usuarios.es_superusuario, la misma autoridad
  // final que ya usa el backend en vfp-sync.js) nunca queda bloqueado por
  // esta regla, tenga o no un rol activo en app_usuario_roles — evita que
  // una cuenta SU con el rol viejo desactivado quede afuera de su propia app.
  if (esSuperUsuario) return <>{children}</>;

  // Regla general: un usuario sin NINGÚN rol activo en Aplicaciones (ej.
  // una cuenta de prueba a la que nunca se le asignó rol) no debería poder
  // entrar a nada operativo solo por tener sesión iniciada — antes esto no
  // se validaba en ningún lado y cualquier cuenta sin rol veía casi todo.
  if (permisos.roles.length === 0 && !RUTAS_SIN_ROL.includes(route)) {
    return (
      <div className="p-6">
        <p style={{ color: 'var(--ink-faint)', fontSize: '13px' }}>
          Tu usuario no tiene ningún rol asignado en Aplicaciones — pedile a un administrador que te lo asigne en Configuración → Roles.
        </p>
      </div>
    );
  }

  const requerido = ROUTE_PERMISSION[route];
  if (!requerido) return <>{children}</>;
  const lista = Array.isArray(requerido) ? requerido : [requerido];
  const tienePermiso = lista.some((flag) => permisos[flag]);
  if (!tienePermiso) {
    return (
      <div className="p-6">
        <p style={{ color: 'var(--ink-faint)', fontSize: '13px' }}>
          Esta pantalla requiere un permiso que tu usuario no tiene.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

// Misma regla de acceso que RouteGate, pero aplicada al árbol del menú
// lateral: antes el candado solo bloqueaba el contenido de la pantalla,
// pero el menú seguía listando TODAS las opciones sin importar el rol
// (ej. un usuario sin V°B° ni muestreo igual veía "Programa de Muestreos"
// en la lista, aunque al entrar le bloqueara el contenido).
function puedeVerRutaMenu(route: AppRoute, permisos: AppPermisos, esSuperUsuario: boolean): boolean {
  if (esSuperUsuario) return true;
  if (permisos.roles.length === 0) return RUTAS_SIN_ROL.includes(route);
  const requerido = ROUTE_PERMISSION[route];
  if (!requerido) return true;
  const lista = Array.isArray(requerido) ? requerido : [requerido];
  return lista.some((flag) => permisos[flag]);
}

function filtrarMenuPorPermisos(menu: MenuItem[], permisos: AppPermisos, esSuperUsuario: boolean): MenuItem[] {
  return menu
    .map((item) => {
      if (item.children) {
        const children = item.children.filter((c) => puedeVerRutaMenu(c.route, permisos, esSuperUsuario));
        if (children.length === 0) return null;
        return { ...item, children };
      }
      if (item.route && !puedeVerRutaMenu(item.route, permisos, esSuperUsuario)) return null;
      return item;
    })
    .filter((item): item is MenuItem => item !== null);
}

function MenuFiltrado({ children }: { children: (menu: MenuItem[]) => React.ReactNode }) {
  const permisos = useAppPermisos();
  const { esSuperUsuario, loading: cargandoSU } = useEsSuperUsuario();
  if (permisos.loading || cargandoSU) return null;
  const menuFiltrado = filtrarMenuPorPermisos(MENU, permisos, esSuperUsuario);
  return <>{children(menuFiltrado)}</>;
}

async function loadEmpresaBranding(empresaId: number) {
  const fallback = {
    empresaNombre: 'Aplicaciones de Campo',
    nombrePlanta: 'Sistema MYA',
    empresaCodigo: String(empresaId || '').padStart(2, '0'),
    logoUrl: '',
  };

  try {
    const [configResult, empresaResult] = await Promise.all([
      supabase.from('fe_config_empresa').select('*').eq('empresa_id', empresaId).maybeSingle(),
      supabase.from('empresas').select('*').eq('id', empresaId).maybeSingle(),
    ]);

    const cfg = (configResult.data || {}) as Record<string, any>;
    const emp = (empresaResult.data || {}) as Record<string, any>;

    return {
      empresaNombre:
        String(emp.nombre || cfg.nombre_empresa || cfg.empresa_nombre || fallback.empresaNombre).trim() ||
        fallback.empresaNombre,
      nombrePlanta:
        String(cfg.nombre_planta || cfg.nombre || emp.nombre || fallback.nombrePlanta).trim() ||
        fallback.nombrePlanta,
      empresaCodigo:
        String(emp.codigo || cfg.codigo || cfg.empresa_codigo || fallback.empresaCodigo).trim() ||
        fallback.empresaCodigo,
      logoUrl: String(cfg.logo_url || '').trim(),
    };
  } catch {
    return fallback;
  }
}

async function fetchApiWithFallback(path: string, init?: RequestInit) {
  const endpoints = buildApiCandidates(path);
  for (const endpoint of endpoints) {
    try {
      return await fetch(endpoint, init);
    } catch {
      // try next candidate
    }
  }
  return null;
}

export function App() {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);
  const [showIntro, setShowIntro] = useState(() => {
    const env = String(process.env.NODE_ENV || '').toLowerCase();
    // En desarrollo local abrimos directo al login para evitar bloqueos por splash.
    return env === 'production';
  });
  const [introExiting, setIntroExiting] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    const hash = window.location.hash || '';
    return search.get('type') === 'recovery' || search.has('token_hash') || search.has('code') || hash.includes('type=recovery');
  });
  // Página pública de aprobación de ajuste (link del email al ingeniero).
  // URL: /aprobacion-ajuste/<token> → muestra AjusteSolicitudPage SIN login.
  const [ajusteToken] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/aprobacion-ajuste\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  });
  // Misma idea para corrección de fecha-ancla (Estadios).
  // URL: /aprobacion-ancla/<token> → muestra AnclaSolicitudPage SIN login.
  const [anclaToken] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/aprobacion-ancla\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  });
  const [activeRoute, setActiveRoute] = useState<AppRoute>('dashboard');
  const [capturandoPantalla, setCapturandoPantalla] = useState(false);
  const [routeHistory, setRouteHistory] = useState<AppRoute[]>([]);
  // Subtítulo dinámico del navbar (breadcrumb de sub-vista, ej. "Preparar Despacho").
  const [navbarSubtitulo, setNavbarSubtitulo] = useState('');
  // Acción para volver a la raíz de la página actual (ej. salir del despacho a la lista).
  const onVolverRaizRef = React.useRef<(() => void) | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Recuerda el estado del sidebar antes de entrar a una vista ancha, para restaurarlo al salir.
  const sidebarPrevioBodega = React.useRef<boolean | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['Operaciones', 'Muestreos']);
  const [selectedBoleta] = useState<any>(null);

  const [empresaNombre, setEmpresaNombre] = useState('Aplicaciones de Campo');
  const [empresaCodigo, setEmpresaCodigo] = useState('');
  const semanaActual = getSemanaActualLabel();
  const [logoUrl, setLogoUrl] = useState('');

  const [paletteId, setPaletteId] = useState<string>(() => {
    const saved = localStorage.getItem(THEME_KEY) || '';
    const id = PALETTES.some((p) => p.id === saved) ? saved : PALETTES[0].id;
    applyPalette(getPalette(id));
    return id;
  });

  useEffect(() => {
    applyPalette(getPalette(paletteId));
    localStorage.setItem(THEME_KEY, paletteId);
  }, [paletteId]);

  // Vistas que necesitan todo el ancho: Terminal Bodega (táctil) y Estadios
  // (lotes + tabla ancha + panel de cambios). Contraen el sidebar al entrar y
  // restauran el estado previo al salir.
  useEffect(() => {
    const VISTAS_ANCHAS = ['aplicaciones.bodega', 'aplicaciones.estadios'];
    if (VISTAS_ANCHAS.includes(activeRoute)) {
      if (sidebarPrevioBodega.current === null) {
        sidebarPrevioBodega.current = sidebarCollapsed;
        setSidebarCollapsed(true);
      }
    } else if (sidebarPrevioBodega.current !== null) {
      setSidebarCollapsed(sidebarPrevioBodega.current);
      sidebarPrevioBodega.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoute]);

  useEffect(() => {
    const syncRecoveryMode = () => {
      const search = new URLSearchParams(window.location.search);
      const hash = window.location.hash || '';
      setRecoveryMode(search.get('type') === 'recovery' || search.has('token_hash') || search.has('code') || hash.includes('type=recovery'));
    };

    const bootstrapRecovery = async () => {
      const search = new URLSearchParams(window.location.search || '');
      const code = search.get('code');
      const tokenHash = search.get('token_hash');
      const flowType = search.get('type') === 'invite' ? 'invite' : 'recovery';
      try {
        if (tokenHash) {
          const { error } = await supabase.auth.verifyOtp({ type: flowType as any, token_hash: tokenHash });
          if (!error) setRecoveryMode(true);
          return;
        }
        if (!code) return;
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!error) setRecoveryMode(true);
      } catch {
        // El formulario de recuperacion mostrara el mensaje si no hay sesion valida.
      }
    };

    syncRecoveryMode();
    void bootstrapRecovery();
    window.addEventListener('hashchange', syncRecoveryMode);
    window.addEventListener('popstate', syncRecoveryMode);
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
    });
    return () => {
      window.removeEventListener('hashchange', syncRecoveryMode);
      window.removeEventListener('popstate', syncRecoveryMode);
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const restaurarSesion = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const accessToken = String(data.session?.access_token || '').trim();
        if (!accessToken) return;

        const empresaPreferida =
          Number(localStorage.getItem(LAST_EMPRESA_KEY) || 0) ||
          DEFAULT_EMPRESA_ID;
        const resp = await fetchApiWithFallback(`/auth/me?modulo_codigo=pina&empresa_id=${empresaPreferida}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!resp?.ok) return;

        const payload = await resp.json().catch(() => null);
        const empresas = Array.isArray(payload?.empresas_autorizadas) ? payload.empresas_autorizadas : [];
        if (!payload?.ok || !payload?.usuario || empresas.length === 0) return;

        const empresa =
          empresas.find((e: any) => Number(e.id) === empresaPreferida) ||
          empresas[0];
        const empresaId = String(empresa?.id || '').trim();
        if (!empresaId || !active) return;

        localStorage.setItem(LAST_EMPRESA_KEY, empresaId);
        setUsuario({
          ...payload.usuario,
          id: String(payload.usuario.id || ''),
          username: String(payload.usuario.username || ''),
          nombre: String(payload.usuario.nombre || payload.usuario.username || 'Usuario'),
          email: String(payload.usuario.email || ''),
          labor: String(payload.usuario.labor || 'Colaborador'),
          empresa_id: empresaId,
          es_superusuario: Boolean(payload.usuario.es_superusuario),
        });
      } catch {
        // Si no se puede restaurar, se muestra login normal.
      } finally {
        if (active) setCargando(false);
      }
    };

    restaurarSesion();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const empresaId = Number(usuario?.empresa_id || 0);
    if (!empresaId) return;

    let active = true;
    loadEmpresaBranding(empresaId).then((branding) => {
      if (!active) return;
      setEmpresaNombre(branding.empresaNombre);
      setEmpresaCodigo(branding.empresaCodigo);
      setLogoUrl(branding.logoUrl);
    });

    return () => {
      active = false;
    };
  }, [usuario?.empresa_id]);

  const closeIntro = useCallback(() => {
    if (introExiting) return;
    setIntroExiting(true);
    window.setTimeout(() => setShowIntro(false), INTRO_EXIT_MS);
  }, [introExiting]);

  useEffect(() => {
    if (cargando || usuario || !showIntro) return;
    const t = window.setTimeout(() => closeIntro(), INTRO_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [cargando, usuario, showIntro, closeIntro]);

  async function handleLogin(result: LoginResult) {
    const { error: setSessionError } = await supabase.auth.setSession({
      access_token: result.access_token,
      refresh_token: result.refresh_token,
    });
    if (setSessionError) {
      await supabase.auth.signOut().catch(() => {});
      setUsuario(null);
      return;
    }

    const sessionData = await supabase.auth.getSession();
    const accessToken = String(sessionData.data.session?.access_token || '').trim();
    if (!accessToken) {
      await supabase.auth.signOut().catch(() => {});
      setUsuario(null);
      return;
    }

    const empresaId = String(result.usuario?.empresa_id || '').trim();
    if (!empresaId) {
      await supabase.auth.signOut().catch(() => {});
      setUsuario(null);
      return;
    }

    const serverUsuario = {
      ...result.usuario,
      empresa_id: empresaId,
    };
    if (!serverUsuario?.username) {
      await supabase.auth.signOut().catch(() => {});
      setUsuario(null);
      return;
    }

    localStorage.setItem(LAST_EMPRESA_KEY, empresaId);
    setUsuario(serverUsuario);
  }

  async function handleLogout() {
    const sessionData = await supabase.auth.getSession().catch(() => null);
    const accessToken = String(sessionData?.data?.session?.access_token || '').trim();
    if (accessToken) {
      await fetchApiWithFallback('/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => {});
    }

    await supabase.auth.signOut().catch(() => {});
    localStorage.removeItem(LAST_EMPRESA_KEY);
    setUsuario(null);
    setActiveRoute('dashboard');
    setRouteHistory([]);
    setEmpresaNombre('Aplicaciones de Campo');
    setEmpresaCodigo('');
    setLogoUrl('');
  }

  function navigate(route: AppRoute) {
    if (route !== activeRoute) setRouteHistory((prev) => [...prev, activeRoute]);
    setActiveRoute(route);
    setNavbarSubtitulo('');
    onVolverRaizRef.current = null;
    setSidebarOpen(false);
  }

  function handleBack() {
    setSidebarOpen(false);
    // Si estamos en una sub-vista (ej. despacho), "Volver" primero regresa a la
    // raíz de la página (la lista) sin salir de la sección.
    if (onVolverRaizRef.current) {
      onVolverRaizRef.current();
      return;
    }
    setRouteHistory((prev) => {
      if (prev.length > 0) {
        const next = [...prev];
        const previousRoute = next.pop()!;
        setActiveRoute(previousRoute);
        return next;
      }
      setActiveRoute(ROUTE_PARENT[activeRoute] || 'dashboard');
      return [];
    });
  }

  function isGroupExpanded(label: string) {
    return expandedGroups.includes(label);
  }

  function toggleGroup(label: string) {
    setExpandedGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );
  }

  // Label de una ruta cualquiera (item directo o hijo de un grupo).
  function labelDeRuta(route: AppRoute): string {
    if (route === 'dashboard') return 'Inicio';
    for (const item of MENU) {
      if (item.route === route) return item.label;
      const child = item.children?.find((c) => c.route === route);
      if (child) return child.label;
    }
    return 'Inicio';
  }

  // Ruta a la que realmente va el botón "Volver" (misma lógica que handleBack):
  // primero el historial, luego el padre configurado.
  function getBackRoute(): AppRoute {
    if (routeHistory.length > 0) return routeHistory[routeHistory.length - 1];
    return ROUTE_PARENT[activeRoute] || 'dashboard';
  }

  // Label del destino real del "Volver" (coincide texto ↔ destino).
  function getParentLabel(): string {
    // En una sub-vista (hay subtítulo), "Volver" regresa a la raíz de la página actual.
    if (navbarSubtitulo && onVolverRaizRef.current) return labelDeRuta(activeRoute);
    return labelDeRuta(getBackRoute());
  }

  // Label de la ruta activa (para mostrar el título en la barra de navegación).
  function getCurrentLabel(): string {
    return activeRoute === 'dashboard' ? '' : labelDeRuta(activeRoute);
  }

  function renderPage() {
    switch (activeRoute) {
      case 'aplicaciones.estadios':
        return <EstadiosPage usuarioNombre={usuario?.nombre || usuario?.username || ''} />;
      case 'aplicaciones.linea-tiempo':
        return <LineaTiempoLotePage />;
      case 'aplicaciones.boletas':
        return <BoletasAplicacionPage />;
      case 'aplicaciones.manual':
        return <BoletaManualPage />;
      case 'aplicaciones.cedulas':
        return <CedulasAplicacionPage />;
      case 'aplicaciones.aplicar-directo':
        return <CedulasAplicarDirectoPage />;
      case 'aplicaciones.bodega':
        return <BodegaPage />;
      case 'aplicaciones.toma-inventario':
        return <TomaInventarioPage />;
      case 'aplicaciones.boomero':
        return <BoomeroPage />;
      case 'aplicaciones.whatsapp':
        return <WhatsAppSenderPage empresaId={usuario?.empresa_id} />;
      case 'aplicaciones.resumen':
        return <ResumenSemanal />;
      case 'aplicaciones.boleta':
        return selectedBoleta ? (
          <DetalleBoleta
            boleta={selectedBoleta}
            onGuardar={() => navigate('dashboard')}
            onCancelar={() => navigate('dashboard')}
          />
        ) : (
          <ApplicacionesDashboard />
        );
      case 'aplicaciones.parcelas':
        return <ParcelasFinca />;
      case 'aplicaciones.plan-manejo':
        return <PlanManejoPage />;
      case 'aplicaciones.plan-costos':
        return <PlanCostosPage />;
      case 'aplicaciones.productos':
        return <CatalogoProductosPage />;
      case 'aplicaciones.depuracion-inventario':
        return <DepuracionInventarioPage />;
      case 'aplicaciones.auditoria-saldos':
        return <AuditoriaSaldosPage />;
      case 'aplicaciones.lote-bloque':
        return <FincaLoteBloquePage />;
      case 'aplicaciones.fincas':
        return <FincasPage />;
      case 'aplicaciones.estadios-vfp':
        return <EstadiosVfpPage />;
      case 'aplicaciones.ciclos':
        return <CiclosCatalogoPage />;
      case 'labores.catalogo':
        return <LaboresCampoPage />;
      case 'labores.boletas':
        return <BoletasLaboresPage supervisorNombre={usuario?.nombre || usuario?.username || ''} />;
      case 'labores.jornadas':
        return <JornadasLaboresPage />;
      case 'labores.maquinaria':
        return <BoletasMaquinariaPage supervisorNombre={usuario?.nombre || usuario?.username || ''} />;
      case 'labores.costeo-contable':
        return <CosteoContableLaboresPage />;
      case 'labores.lotes-costeo':
        return <LotesCosteoLaboresPage />;
      case 'labores.reporte-jornadas':
        return <ReporteJornadasAprobadasPage />;
      case 'labores.cierre-semanal':
        return <CierreSemanalFincaPage usuarioNombre={usuario?.nombre || usuario?.username || ''} />;
      case 'labores.muestreos':
        return <ProgramaMuestreosPage />;
      case 'labores.mis-muestreos':
        return <MisMuestreosPage />;
      case 'labores.reporte-muestreos':
        return <ReporteMuestreosPage />;
      case 'labores.catalogo-muestreos':
        return <CatalogoLaboresMuestreosPage />;
      case 'reportes.costo-lote':
        return <ReporteCostoLotePage />;
      case 'reportes.activo-biologico':
        return <ActivoBiologicoInsumosPage />;
      case 'reportes.costeo-produccion-ab':
        return <CosteoProduccionABPage />;
      case 'aplicaciones.aplicaciones-manuales':
        return <AplicacionesManualesPage />;
      case 'aplicaciones.config-general':
        return <ConfiguracionGeneral />;
      case 'aplicaciones.cronologia-parametros':
        return <CronologiaParametrosPage />;
      case 'aplicaciones.roles':
        return <ControlPinaRolesPage />;
      case 'aplicaciones.sync-vfp':
        return <SyncVfpPage />;
      case 'aplicaciones.dron-importer':
        return <DronImporter />;
      case 'aplicaciones.dron-mosaico':
        return <DronMosaico />;
      case 'aplicaciones.conteo':
        return <ConteoPlantasV2Page />;
      case 'aplicaciones.conteo-v3-test':
        return <ConteoPlantasV3TestPage />;
      case 'aplicaciones.conteo-v4-simple':
        return <ConteoPlantasV4SimplePage />;
      case 'aplicaciones.conteo-v5-simple':
        return <ConteoPlantasV5SimplePage />;
      case 'aplicaciones.conteo-calibrado':
        return <ConteoCalibradoPage />;
      case 'aplicaciones.conteo-v8':
        return <ConteoPlantasV8Page />;
      default:
        return <ApplicacionesDashboard />;
    }
  }

  if (cargando) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--surface-base)' }}>
        <div className="text-center">
          <Leaf className="mx-auto mb-4" size={34} style={{ color: 'var(--emp-accent-txt)' }} />
          <div
            className="w-8 h-8 border-4 rounded-full animate-spin mx-auto mb-3"
            style={{ borderColor: 'var(--emp-accent)', borderTopColor: 'var(--emp-accent-txt)' }}
          />
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            Cargando...
          </p>
        </div>
      </div>
    );
  }

  // Vista exclusiva de aprobación (link público del email al ingeniero).
  // Va ANTES del login: el ingeniero aprueba sin necesidad de cuenta.
  if (ajusteToken) {
    return <AjusteSolicitudPage token={ajusteToken} />;
  }
  if (anclaToken) {
    return <AnclaSolicitudPage token={anclaToken} />;
  }

  if (recoveryMode) {
    return (
      <RecoveryPassword
        onDone={() => {
          window.history.replaceState({}, document.title, window.location.pathname);
          setRecoveryMode(false);
        }}
      />
    );
  }

  if (!usuario && showIntro) {
    return (
      <div
        className={`intro-splash ${introExiting ? 'is-exiting' : ''}`}
        role="button"
        tabIndex={0}
        onClick={closeIntro}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') closeIntro();
        }}
        aria-label="Omitir presentacion"
      >
        <div className="intro-splash-bg" style={{ backgroundImage: `url(${INTRO_GIF})` }} />
        <div className="intro-splash-overlay" />
        <div className="intro-splash-media">
          <img className="intro-splash-gif" src={INTRO_GIF} alt="Presentacion" />
        </div>
        <div className="intro-splash-content">
          <span>Toca para continuar</span>
        </div>
      </div>
    );
  }
  if (!usuario) return <Login onLogin={handleLogin} />;

  const showBack = activeRoute !== 'dashboard' || routeHistory.length > 0;
  const brandLogoSrc = logoUrl || FALLBACK_LOGO;

  return (
    <EmpresaProvider empresaId={Number(usuario.empresa_id) || 1}>
      <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--emp-bg-main)' }}>
        <header
          className="flex items-center justify-between gap-2 px-3 sm:px-4 shrink-0 sticky top-0 z-40"
          style={{
            backgroundColor: 'var(--emp-bg-panel)',
            borderBottom: '1px solid var(--emp-border)',
            minHeight: '44px',
          }}
        >
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden -m-1.5 flex items-center justify-center rounded" style={{ color: 'var(--ink)', width: 44, height: 44 }}>
              <Menu size={26} strokeWidth={2.25} />
            </button>
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'var(--emp-accent-bg)', border: '1px solid var(--emp-accent)' }}
              >
                <img
                  src={brandLogoSrc}
                  alt="logo empresa"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    if (img.src === FALLBACK_LOGO) {
                      img.style.display = 'none';
                    } else {
                      img.src = FALLBACK_LOGO;
                    }
                  }}
                />
              </div>
              <div className="min-w-0">
                <p className="truncate font-bold text-base sm:text-lg leading-tight" style={{ color: 'var(--ink)' }}>
                  {empresaNombre}
                </p>
                <div className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--ink-faint)' }}>
                  {empresaCodigo && (
                    <span
                      className="font-mono font-bold px-1.5 py-0.5 rounded shrink-0"
                      style={{
                        color: 'var(--emp-accent-txt)',
                        background: 'var(--emp-accent-bg)',
                        border: '1px solid var(--emp-accent)',
                      }}
                    >
                      {empresaCodigo}
                    </span>
                  )}
                  <span
                    className="font-mono font-bold px-1.5 py-0.5 rounded shrink-0"
                    title="Semana actual"
                    style={{
                      color: 'var(--tone-info-text)',
                      background: 'var(--tone-info-bg)',
                      border: '1px solid var(--tone-info-border)',
                    }}
                  >
                    Sem. {semanaActual}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <ThemeSwitcher paletteId={paletteId} onChange={setPaletteId} compact />
            <div style={{ width: 1, height: 20, background: 'var(--emp-border)' }} className="hidden sm:block" />
            <div className="hidden sm:flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{
                  background: 'var(--emp-accent-bg)',
                  color: 'var(--emp-accent-txt)',
                  border: '1px solid var(--emp-accent)',
                }}
              >
                {(usuario.nombre || usuario.username || 'U').charAt(0).toUpperCase()}
              </div>
              <span className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
                {usuario.nombre || usuario.username}
              </span>
            </div>
            <div style={{ width: 1, height: 20, background: 'var(--emp-border)' }} className="hidden sm:block" />
            <button
              onClick={handleLogout}
              className="flex shrink-0 items-center justify-center gap-1.5 text-xs rounded transition-colors"
              style={{ color: 'var(--ink-faint)', minWidth: 44, minHeight: 44 }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = '#f87171';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'var(--ink-faint)';
              }}
            >
              <LogOut size={18} className="sm:hidden" />
              <LogOut size={13} className="hidden sm:block" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {sidebarOpen && (
            <div className="fixed inset-0 bg-black/70 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />
          )}

          <aside
            className={`
              fixed top-[44px] left-0 h-[calc(100vh-44px)] z-30 flex flex-col
              transform transition-all duration-200
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
              md:translate-x-0 md:static md:h-auto md:z-auto
            `}
            style={{
              width: sidebarCollapsed ? '3.5rem' : '18rem',
              backgroundColor: 'var(--emp-bg-panel)',
              borderRight: '1px solid var(--emp-border)',
            }}
          >
            <div
              className={`relative flex items-center py-3 ${sidebarCollapsed ? 'justify-center px-0' : 'gap-2.5 px-4'}`}
              style={{ borderBottom: '1px solid var(--emp-border)' }}
            >
              <div
                className="w-8 h-8 shrink-0 flex items-center justify-center"
                title={sidebarCollapsed ? empresaNombre : undefined}
              >
                <img
                  src={FALLBACK_LOGO}
                  alt="logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    img.style.display = 'none';
                  }}
                />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <p className="font-semibold text-xs leading-tight" style={{ color: 'var(--ink)' }}>
                    Control de Piña
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>
                    Aplicaciones de Campo
                  </p>
                </div>
              )}
              <button
                className="absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border p-0.5 transition md:flex"
                style={{
                  backgroundColor: 'var(--emp-bg-panel)',
                  borderColor: 'var(--emp-border)',
                  color: 'var(--ink-muted)',
                }}
                onClick={() => setSidebarCollapsed((c) => !c)}
                type="button"
                title={sidebarCollapsed ? 'Expandir menu' : 'Colapsar menu'}
              >
                {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
              </button>
            </div>

            <nav className={`flex-1 overflow-y-auto py-3 space-y-0.5 ${sidebarCollapsed ? 'px-1' : 'px-2'}`}>
              {!sidebarCollapsed && expandedGroups.length > 0 && (
                <button
                  onClick={() => setExpandedGroups([])}
                  className="w-full flex items-center justify-center gap-1.5 mb-1 px-2 py-1 rounded text-[10px] font-medium transition-colors"
                  style={{ color: 'var(--ink-faint)', background: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--emp-accent-txt)'; e.currentTarget.style.background = 'var(--emp-accent-bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ink-faint)'; e.currentTarget.style.background = 'transparent'; }}
                  title="Contraer todos los grupos"
                >
                  <ChevronsDownUp size={12} /> Contraer todos
                </button>
              )}
              <MenuFiltrado>
              {(menuFiltrado) => menuFiltrado.map((item) => {
                const expanded = isGroupExpanded(item.label);
                const isChildActive = item.children?.some((c) => c.route === activeRoute) ?? false;
                const isDirectActive = item.route === activeRoute;
                const itemActive = isChildActive || isDirectActive;

                if (sidebarCollapsed) {
                  return (
                    <button
                      key={item.id}
                      onClick={() =>
                        item.children ? navigate(item.children[0].route) : item.route && navigate(item.route)
                      }
                      title={item.label}
                      className="w-full flex justify-center items-center px-0 py-2.5 rounded text-xs font-medium transition-colors"
                      style={{
                        color: itemActive ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                        background: itemActive ? 'var(--emp-accent-bg)' : 'transparent',
                        borderLeft: itemActive
                          ? '2px solid var(--emp-accent)'
                          : '2px solid transparent',
                      }}
                    >
                      {item.icon}
                    </button>
                  );
                }

                return (
                  <div key={item.id}>
                    <button
                      onClick={() => item.children ? toggleGroup(item.label) : item.route && navigate(item.route)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded text-xs font-semibold transition-colors"
                      style={{
                        color: itemActive ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                        // Título de grupo: fondo tenue permanente (surface-raised se eleva
                        // sobre el panel del sidebar en cualquier paleta/tema).
                        background: itemActive ? 'var(--emp-accent-bg)' : 'var(--surface-raised)',
                        border: itemActive ? '1px solid var(--emp-accent)' : '1px solid var(--line-dim)',
                        borderLeft: itemActive ? '3px solid var(--emp-accent)' : '3px solid transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!itemActive) {
                          e.currentTarget.style.background = 'var(--emp-accent-bg)';
                          e.currentTarget.style.color = 'var(--emp-accent-txt)';
                          e.currentTarget.style.borderLeftColor = 'var(--emp-accent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!itemActive) {
                          e.currentTarget.style.background = 'var(--surface-raised)';
                          e.currentTarget.style.color = 'var(--ink-muted)';
                          e.currentTarget.style.borderLeftColor = 'transparent';
                        }
                      }}
                    >
                      {item.icon}
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.children && (expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
                    </button>
                    {expanded && item.children && (
                      <div
                        className="ml-2 mt-0.5 space-y-0.5"
                        style={{ borderLeft: '1px solid var(--emp-border)', paddingLeft: '10px' }}
                      >
                        {item.children.map((child) => {
                          const active = activeRoute === child.route;
                          return (
                            <button
                              key={child.route}
                              onClick={() => navigate(child.route)}
                              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px] font-medium transition-colors text-left"
                              style={{
                                color: active ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                                background: active ? 'var(--emp-accent-bg)' : 'transparent',
                                border: active ? '1px solid var(--emp-accent)' : '1px solid transparent',
                                borderLeft: active ? '3px solid var(--emp-accent)' : '3px solid transparent',
                              }}
                              onMouseEnter={(e) => {
                                if (!active) {
                                  e.currentTarget.style.background = 'var(--emp-accent-bg)';
                                  e.currentTarget.style.color = 'var(--emp-accent-txt)';
                                  // Borde izq de acento: da presencia clara aun en tema claro,
                                  // donde el fondo accent-bg es muy pálido.
                                  e.currentTarget.style.borderLeft = '3px solid var(--emp-accent)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!active) {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = 'var(--ink-muted)';
                                  e.currentTarget.style.borderLeft = '3px solid transparent';
                                }
                              }}
                            >
                              {child.icon}
                              <span className="truncate">{child.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              </MenuFiltrado>
            </nav>
          </aside>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {showBack && (
              <div className="px-4 sm:px-6 pt-4 pb-1 flex-shrink-0 flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors flex-shrink-0"
                  style={{
                    borderColor: 'var(--emp-border)',
                    backgroundColor: 'var(--emp-bg-panel)',
                    color: 'var(--ink-muted)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--emp-accent-txt)';
                    e.currentTarget.style.borderColor = 'var(--emp-accent)';
                    e.currentTarget.style.backgroundColor = 'var(--emp-accent-bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--ink-muted)';
                    e.currentTarget.style.borderColor = 'var(--emp-border)';
                    e.currentTarget.style.backgroundColor = 'var(--emp-bg-panel)';
                  }}
                >
                  <span aria-hidden="true">&larr;</span>
                  <span>Volver</span>
                  <span className="hidden sm:inline" style={{ color: 'var(--ink-faint)' }}>
                    a {getParentLabel()}
                  </span>
                </button>
                {getCurrentLabel() && (
                  <h1 className="flex min-w-0 items-center gap-1.5 truncate text-sm font-bold sm:text-base" style={{ color: 'var(--ink)' }}>
                    {navbarSubtitulo ? (
                      // Con subtítulo, el label de la ruta es clickeable: vuelve a la raíz de la página.
                      <button
                        type="button"
                        className="truncate transition-colors"
                        style={{ color: 'var(--ink-muted)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--emp-accent-txt)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ink-muted)'; }}
                        onClick={() => { onVolverRaizRef.current?.(); }}
                      >
                        {getCurrentLabel()}
                      </button>
                    ) : (
                      <span className="truncate">{getCurrentLabel()}</span>
                    )}
                    {navbarSubtitulo && (
                      <>
                        <span style={{ color: 'var(--ink-faint)' }}>›</span>
                        <span className="truncate font-semibold" style={{ color: 'var(--ink)' }}>{navbarSubtitulo}</span>
                      </>
                    )}
                  </h1>
                )}
              </div>
            )}
            <main className="flex-1 min-h-0 overflow-auto">
              <NavbarSubtituloProvider api={{ setSubtitulo: setNavbarSubtitulo, setOnVolverRaiz: (fn) => { onVolverRaizRef.current = fn; } }}>
                <RouteGate route={activeRoute}>{renderPage()}</RouteGate>
              </NavbarSubtituloProvider>
            </main>
          </div>

          {sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="fixed top-14 right-4 z-40 md:hidden rounded-full p-1.5"
              style={{
                background: 'var(--emp-bg-panel)',
                border: '1px solid var(--emp-border)',
                color: 'var(--ink-muted)',
              }}
            >
              <X size={15} />
            </button>
          )}

          {/* Acceso directo flotante a WhatsApp (Enviar mensaje) — visible en
              toda la app, se oculta en la propia pantalla de WhatsApp. */}
          {activeRoute !== 'aplicaciones.whatsapp' && (
            <button
              type="button"
              disabled={capturandoPantalla}
              onClick={async () => {
                setCapturandoPantalla(true);
                try {
                  const main = document.querySelector('main');
                  if (main) {
                    const canvas = await html2canvas(main as HTMLElement, {
                      backgroundColor: getComputedStyle(document.body).backgroundColor || '#0b1220',
                      scale: 1.5, useCORS: true, logging: false,
                    });
                    const blob: Blob | null = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
                    if (blob) setCapturaPendiente(new File([blob], 'captura-pantalla.jpg', { type: 'image/jpeg' }));
                  }
                } catch {
                  // Si la captura falla, se navega igual sin adjunto — no bloquear el envío.
                } finally {
                  setCapturandoPantalla(false);
                  navigate('aplicaciones.whatsapp');
                }
              }}
              title="Enviar mensaje por WhatsApp (adjunta una captura de esta pantalla)"
              className="fixed bottom-5 right-5 z-40 flex items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 disabled:opacity-70"
              style={{ width: 52, height: 52, background: '#16a34a', color: '#fff' }}
            >
              {capturandoPantalla ? <RefreshCw size={22} className="animate-spin" /> : <MessageCircle size={24} />}
            </button>
          )}
        </div>
      </div>
    </EmpresaProvider>
  );
}

export default App;
